/** Common IATA → city labels for booking flight details (fallback: code). */
const AIRPORT_CITY: Record<string, string> = {
  JFK: 'New York',
  LGA: 'New York',
  EWR: 'New York',
  ORD: 'Chicago',
  MDW: 'Chicago',
  LAX: 'Los Angeles',
  SFO: 'San Francisco',
  MIA: 'Miami',
  FLL: 'Miami',
  ATL: 'Atlanta',
  DFW: 'Dallas',
  DAL: 'Dallas',
  DEN: 'Denver',
  SEA: 'Seattle',
  LAS: 'Las Vegas',
  PHX: 'Phoenix',
  BOS: 'Boston',
  IAD: 'Washington',
  DCA: 'Washington',
  BWI: 'Washington',
  PHL: 'Philadelphia',
  DTW: 'Detroit',
  MSP: 'Minneapolis',
  CLT: 'Charlotte',
  HNL: 'Honolulu',
  LHR: 'London',
  LGW: 'London',
  STN: 'London',
  CDG: 'Paris',
  ORY: 'Paris',
  FCO: 'Rome',
  MXP: 'Milan',
  AMS: 'Amsterdam',
  FRA: 'Frankfurt',
  MUC: 'Munich',
  MAD: 'Madrid',
  BCN: 'Barcelona',
  DUB: 'Dublin',
  ZRH: 'Zurich',
  VIE: 'Vienna',
  DXB: 'Dubai',
  HKG: 'Hong Kong',
  NRT: 'Tokyo',
  HND: 'Tokyo',
  ICN: 'Seoul',
  SIN: 'Singapore',
  SYD: 'Sydney',
  YYZ: 'Toronto',
  YVR: 'Vancouver',
  MEX: 'Mexico City',
};

/** Full airport names for booking “click code to expand”. */
const AIRPORT_FULL_NAME: Record<string, string> = {
  JFK: 'John F Kennedy International Airport',
  LGA: 'LaGuardia Airport',
  EWR: 'Newark Liberty International Airport',
  ORD: "O'Hare International Airport",
  MDW: 'Chicago Midway International Airport',
  LAX: 'Los Angeles International Airport',
  SFO: 'San Francisco International Airport',
  MIA: 'Miami International Airport',
  FLL: 'Fort Lauderdale-Hollywood International Airport',
  ATL: 'Hartsfield-Jackson Atlanta International Airport',
  DFW: 'Dallas Fort Worth International Airport',
  DAL: 'Dallas Love Field',
  DEN: 'Denver International Airport',
  SEA: 'Seattle-Tacoma International Airport',
  LAS: 'Harry Reid International Airport',
  PHX: 'Phoenix Sky Harbor International Airport',
  BOS: 'Boston Logan International Airport',
  IAD: 'Washington Dulles International Airport',
  DCA: 'Ronald Reagan Washington National Airport',
  BWI: 'Baltimore/Washington International Airport',
  PHL: 'Philadelphia International Airport',
  DTW: 'Detroit Metropolitan Wayne County Airport',
  MSP: 'Minneapolis-Saint Paul International Airport',
  CLT: 'Charlotte Douglas International Airport',
  HNL: 'Daniel K Inouye International Airport',
  LHR: 'London Heathrow Airport',
  LGW: 'London Gatwick Airport',
  STN: 'London Stansted Airport',
  CDG: 'Paris Charles de Gaulle Airport',
  ORY: 'Paris Orly Airport',
  FCO: 'Leonardo da Vinci-Fiumicino Airport',
  MXP: 'Milan Malpensa Airport',
  AMS: 'Amsterdam Airport Schiphol',
  FRA: 'Frankfurt Airport',
  MUC: 'Munich Airport',
  MAD: 'Adolfo Suárez Madrid-Barajas Airport',
  BCN: 'Barcelona-El Prat Airport',
  DUB: 'Dublin Airport',
  ZRH: 'Zurich Airport',
  VIE: 'Vienna International Airport',
  DXB: 'Dubai International Airport',
  HKG: 'Hong Kong International Airport',
  NRT: 'Narita International Airport',
  HND: 'Tokyo Haneda Airport',
  ICN: 'Incheon International Airport',
  SIN: 'Singapore Changi Airport',
  SYD: 'Sydney Kingsford Smith Airport',
  YYZ: 'Toronto Pearson International Airport',
  YVR: 'Vancouver International Airport',
  MEX: 'Mexico City International Airport',
};

export function normalizeAirportCode(value?: string): string {
  const v = (value ?? '').trim().toUpperCase();
  return /^[A-Z]{3}$/.test(v) ? v : '';
}

export function airportCityLabel(value?: string): string {
  const code = normalizeAirportCode(value);
  if (code) return AIRPORT_CITY[code] ?? code;
  const raw = (value ?? '').trim();
  return raw || '—';
}

export function airportCodeLabel(value?: string): string {
  return normalizeAirportCode(value) || (value ?? '').trim() || '—';
}

/** Full airport name for expand-on-click in booking UI. */
export function airportFullName(value?: string): string {
  const code = normalizeAirportCode(value);
  if (code && AIRPORT_FULL_NAME[code]) return AIRPORT_FULL_NAME[code];
  const city = code ? AIRPORT_CITY[code] : '';
  if (city && city !== code) return `${city} International Airport`;
  const raw = (value ?? '').trim();
  return raw || '—';
}

/** Label shown when user expands an airport code (never repeats the code alone). */
export function airportExpandLabel(value?: string): string {
  const code = normalizeAirportCode(value);
  const full = airportFullName(value);
  if (code && full.toUpperCase() !== code) return full;
  const city = code ? airportCityLabel(value) : '';
  if (city && city.toUpperCase() !== code) return `${city} International Airport`;
  return full;
}

export function airportExpandDiffersFromCode(value?: string): boolean {
  const code = normalizeAirportCode(value) || (value ?? '').trim().toUpperCase();
  if (!code) return false;
  return airportExpandLabel(value).toUpperCase() !== code;
}
